import { Fragment, useEffect, useMemo, useState, type CSSProperties, type FormEvent } from 'react';

import { ContactPage } from './components/ContactPage';
import { LegalPage } from './components/LegalPage';
import { WithdrawalPage } from './components/WithdrawalPage';
import { legalFooterLinks, productFooterLinks } from './data/footerLinks';
import { getLegalDocument } from './data/legalDocuments';
import { parseWaitlistResponse } from './lib/waitlist-client';
import type { WaitlistField } from './lib/waitlist';

interface OpportunityGap {
  label: string;
  remediation: string;
  estimate?: string;
}

interface Opportunity {
  id: string;
  title: string;
  meta: string;
  status: string;
  dot: string;
  chipBg: string;
  chipFg: string;
  confidence: string;
  readiness?: number;
  tasks?: { done: number; total: number; offPlatform: number };
  why: string;
  gaps: OpportunityGap[];
  next: string;
  sources: Array<[string, string]>;
}

const opportunities: Opportunity[] = [
  {
    id: '1',
    title: 'Digitális fejlesztési program — KKV',
    meta: 'Eszköz · szoftver · határidő: szept. 30.',
    status: 'Jogosult',
    dot: '#2E7D5B',
    chipBg: '#E3EFE8',
    chipFg: '#1F6147',
    confidence: 'Magas bizonyosság',
    readiness: 82,
    tasks: { done: 9, total: 11, offPlatform: 2 },
    why: 'Minden kötelező jogosultsági szabály teljesül: a vállalatméret, a főtevékenység és a tervezett eszközbeszerzés is a támogatható körbe esik.',
    gaps: [],
    next: 'Beadási csomag előkészítése és a mellékletek véglegesítése.',
    sources: [
      ['Felhívás 1.1', 'Jogosultsági feltételek'],
      ['Felhívás 4.2', 'Támogatható tevékenységek'],
      ['Útmutató 2.4', 'Elszámolható költségek']
    ]
  },
  {
    id: '2',
    title: 'Energiahatékonysági beruházás',
    meta: 'Napelem · szigetelés · határidő: okt. 15.',
    status: 'Pótolható hiány',
    dot: '#C0883A',
    chipBg: '#F6EAD6',
    chipFg: '#8A5A1E',
    confidence: 'Közepes bizonyosság',
    readiness: 64,
    tasks: { done: 5, total: 8, offPlatform: 2 },
    why: 'A beruházás típusa és a főtevékenység támogatható, két kötelező adat azonban még hiányzik a végleges jogosultsági döntéshez.',
    gaps: [
      { label: 'Projekt megvalósítási helyszíne', remediation: 'Pótolható', estimate: '~1 nap' },
      { label: 'Legutóbbi lezárt év árbevétele', remediation: 'Pótolható', estimate: '~2 nap' }
    ],
    next: 'Pénzügyi profil kiegészítése a céges dossziéban.',
    sources: [
      ['Felhívás 1.1', 'Jogosultsági feltételek'],
      ['3. melléklet', 'Pénzügyi adatlap'],
      ['Útmutató 2.4', 'Elszámolható költségek']
    ]
  },
  {
    id: '3',
    title: 'Közúti infrastruktúra — beszállító',
    meta: 'Alvállalkozói keret · közbeszerzés',
    status: 'Beszállítói út',
    dot: '#3E6291',
    chipBg: '#E4EAF2',
    chipFg: '#2E4C73',
    confidence: 'Magas bizonyosság',
    why: 'Pályázóként nem jogosult, de a nyertes kivitelezők beszállítói keretében a profil releváns — a rendszer ezt külön útvonalként jelzi.',
    gaps: [{ label: 'Beszállítói referencia-összefoglaló', remediation: 'Pótolható', estimate: '~3 nap' }],
    next: 'Beszállítói profil összeállítása.',
    sources: [
      ['Felhívás 2.3', 'Közbeszerzési feltételek'],
      ['Útmutató 5.1', 'Alvállalkozói részvétel']
    ]
  },
  {
    id: '4',
    title: 'Korai fázisú tőkeprogram',
    meta: 'Magvető befektetés · data room',
    status: 'Befektetési út',
    dot: '#6B5B95',
    chipBg: '#ECE8F3',
    chipFg: '#4E4476',
    confidence: 'Közepes bizonyosság',
    why: 'Nem klasszikus pályázat: kockázati tőke jellegű program, befektetés-érettségi útvonalon haladva releváns.',
    gaps: [
      { label: 'Data room alapdokumentumok', remediation: 'Pótolható', estimate: '~2 hét' },
      { label: 'Cap table és pitch összefoglaló', remediation: 'Pótolható' }
    ],
    next: 'Befektetés-érettségi lépések indítása.',
    sources: [
      ['Program 1.0', 'Befektetési kritériumok'],
      ['Útmutató 3.2', 'Data room elvárások']
    ]
  },
  {
    id: '5',
    title: 'Regionális KKV keret',
    meta: 'Forrás ellenőrzés alatt',
    status: 'Ellenőrzés alatt',
    dot: '#8A93A6',
    chipBg: '#EBEDF1',
    chipFg: '#5C6678',
    confidence: 'Alacsony bizonyosság',
    why: 'A relevancia valószínű, de a forrásszöveg pontosságát szakértő ellenőrzi, mielőtt a rendszer jogosultsági állapotot adna ki.',
    gaps: [{ label: 'Megerősített forráshivatkozás', remediation: 'Ellenőrzendő' }],
    next: 'Szakértői forrás-ellenőrzésre várakozás.',
    sources: [['Felhívás ?', 'Ellenőrzés alatt']]
  }
];

const mockNavItems: Array<{ section?: string; icon: string; label: string; count?: number; active?: boolean }> = [
  { section: 'Munkatér', icon: 'gauge', label: 'Mai áttekintés', active: true },
  { icon: 'search', label: 'Pályázatok', count: 11 },
  { icon: 'doc', label: 'Projektek' },
  { icon: 'folder', label: 'Céges dosszié' },
  { icon: 'checks', label: 'Teendők', count: 4 },
  { section: 'Eszközök', icon: 'vault', label: 'Data room' },
  { icon: 'shield', label: 'Trust Center', count: 1 }
];

const painItems = [
  'Hosszú felhívások',
  'Mellékletek, segédletek',
  'Nehezen értelmezhető kizáró okok',
  'Határidők és beadási ablakok',
  'Hiányzó dokumentumok',
  'Nem egyértelmű jogosultság',
  'Mi pótolható és mi végleges akadály?',
  'Túl sok pályázat, ami nem neked szól'
];

const processSteps = [
  {
    title: 'Céges dosszié',
    body: 'Összegezzük a fontos adatokat: méret, székhely, tevékenységek, megfelelőségi információk, stb.',
    visual: 'folder'
  },
  {
    title: 'Projekt összefoglaló',
    body: 'Megadod, mit szeretnél megvalósítani: beruházás, K+F, finanszírozás, beszállítói lehetőség, minősítés vagy startup-befektetés.',
    visual: 'chips'
  },
  {
    title: 'Pályázati matching',
    body: 'Az Orienta összeveti a dossziét és a projektet a feldolgozott pályázati szabályrendszerrel.',
    visual: 'match'
  },
  {
    title: 'Akcióterv',
    body: 'Megkapod, mire vagy kész, mi hiányzik, mit kell feltölteni, mit kell külső platformon elintézni, és mi a következő lépés.',
    visual: 'checklist'
  }
];

const interests = [
  'Pályázati előszűrés',
  'Hitel / finanszírozás',
  'Startup / kockázati tőke',
  'Beszállítói lehetőségek',
  'Tanácsadói használat',
  'Egyéb'
];

const initialForm = {
  name: '',
  email: '',
  company: '',
  role: '',
  companyType: '',
  interest: '',
  website: ''
};

function App() {
  const pathname = window.location.pathname.replace(/\/+$/, '') || '/';
  const legalDocument = getLegalDocument(pathname);

  if (pathname === '/kapcsolat') {
    return <ContactPage />;
  }

  if (pathname === '/elallas') {
    return <WithdrawalPage />;
  }

  if (legalDocument) {
    return <LegalPage document={legalDocument} />;
  }

  return <LandingPage />;
}

function LandingPage() {
  useRevealOnScroll();
  useHeroChoreography();

  return (
    <>
      <a className="skip-link" href="#main">
        Ugrás a tartalomra
      </a>
      <SiteNav />
      <main id="main">
        <HeroSection />
        <PainSection />
        <PromiseSection />
        <HowItWorksSection />
        <BetaAccessSection />
        <TrustSection />
        <BeforeAfterSection />
        <FinalCtaSection />
      </main>
      <SiteFooter />
    </>
  );
}

function SiteNav() {
  return (
    <nav className="site-nav" aria-label="Fő navigáció">
      <div className="nav-inner">
        <a href="#top" className="brand-link" aria-label="Orienta főoldal">
          <Logo />
          <span>Orienta</span>
        </a>
        <div className="nav-links" data-nav-links>
          <a href="#funkciok">Funkciók</a>
          <a href="#hogyan">Hogyan működik</a>
          <a href="#megbizhatosag">Megbízhatóság</a>
          <a href="https://palyazat.orientaplatform.eu">Bejelentkezés</a>
        </div>
        <a className="nav-cta" href="#beta">
          Béta várólista
        </a>
      </div>
    </nav>
  );
}

function HeroSection() {
  return (
    <header id="top" className="hero">
      <div id="hero-stage" className="hero-stage">
        <div className="hero-glow" aria-hidden="true" />
        <div className="hero-frame">
          <div id="hero-text" className="hero-copy">
            <h1>
              <strong>Találd meg a cégednek való pályázatokat</strong> — <em>érthetően.</em>
            </h1>
            <p>
              Az Orienta a céges adataid és projektötleted alapján megmutatja, mely pályázatok
              relevánsak, mihez kell még könnyen kivitelezhető módosítás, és mi az, amit érdemes
              azonnal elengedni.
            </p>
            <div className="promise-pill" aria-label="Orienta pozicionálás">
              <span>Nem pályázati adatbázis.</span>
              <strong>Döntéstámogató rendszer.</strong>
            </div>
            <div className="hero-actions">
              <a className="primary-button" href="#beta">
                Csatlakozom a béta várólistához
                <ArrowIcon />
              </a>
              <a className="secondary-button" href="#hogyan">
                Megnézem, hogyan működik
              </a>
            </div>
            <p className="trust-note">
              <ShieldIcon />
              Kontrollált béta indul szakértői felülvizsgálattal.
            </p>
          </div>
          <div id="hero-mock" className="hero-mock">
            <p id="hero-mocktitle" className="mock-title">
              Ez nem egy újabb pályázati felsorolás.
            </p>
            <ProductMockup />
          </div>
        </div>
      </div>
    </header>
  );
}

function ProductMockup() {
  const [selectedId, setSelectedId] = useState('2');
  const selected = useMemo(
    () => opportunities.find((opportunity) => opportunity.id === selectedId) ?? opportunities[1],
    [selectedId]
  );

  return (
    <section className="product-window" aria-label="Orienta termék előnézet">
      <div className="window-bar">
        <div className="window-title">
          <Logo small />
          <span className="window-brand">Orienta</span>
          <span className="window-separator" />
          <span className="mono-muted">Munkatér / Mai áttekintés</span>
        </div>
        <div className="window-tools" aria-hidden="true">
          <span className="search-chip">Keresés…</span>
          <span className="avatar">AG</span>
        </div>
      </div>
      <div className="mock-shell">
        <nav className="mock-nav" aria-hidden="true">
          {mockNavItems.map((item) => (
            <Fragment key={item.label}>
              {item.section ? <span className="mock-nav-section">{item.section}</span> : null}
              <span className={`mock-nav-item ${item.active ? 'active' : ''}`}>
                <NavIcon type={item.icon} />
                {item.label}
                {item.count ? <b>{item.count}</b> : null}
              </span>
            </Fragment>
          ))}
          <span className="mock-nav-note">
            <strong>Előkészítő platform</strong>
            Forrásolt döntések és beadás-előkészítés. A hivatalos beadás a pályázati portálon történik.
          </span>
        </nav>
        <div className="mock-main">
          <div className="company-row">
            <div className="company-meta">
              <span className="company-avatar">AG</span>
              <span>
                <strong>Acme Gyártó Kft.</strong>
                <small>Frissítve: ma · 29 feldolgozott felhívás · szabálymotor</small>
              </span>
            </div>
            <div className="filters" aria-label="Lehetőség szűrők">
              <span className="filter active">Releváns 11</span>
              <span className="filter">Jogosult 3</span>
              <span className="filter">Pótolható 5</span>
              <span className="filter">Mind 29</span>
            </div>
          </div>
          <div className="stat-strip" aria-label="Találati összesítő">
            <Stat dot="#2E7D5B" value="3" label="JOGOSULT" />
            <Stat dot="#C0883A" value="5" label="PÓTOLHATÓ HIÁNY" />
            <Stat dot="#3E6291" value="2" label="BESZÁLLÍTÓI ÚT" />
            <Stat dot="#6B5B95" value="1" label="BEFEKTETÉSI ÚT" />
            <Stat dot="#B4453A" value="18" label="KIZÁRÓ OK" muted />
          </div>
          <div className="mock-body">
            <div className="opportunity-list">
              <div className="list-head">
                <strong>Rangsorolt lehetőségek</strong>
                <span>29 közül 11 releváns</span>
              </div>
              {opportunities.map((opportunity) => (
                <button
                  key={opportunity.id}
                  className={`opportunity-row ${selectedId === opportunity.id ? 'selected' : ''}`}
                  type="button"
                  style={{ '--status-color': opportunity.dot } as CSSProperties}
                  onClick={() => setSelectedId(opportunity.id)}
                >
                  <span className="row-top">
                    <strong>{opportunity.title}</strong>
                    <StatusChip opportunity={opportunity} />
                  </span>
                  <span className="row-meta">
                    <span>{opportunity.meta}</span>
                    {typeof opportunity.readiness === 'number' ? (
                      <>
                        <span className="progress">
                          <span style={{ width: `${opportunity.readiness}%`, background: opportunity.dot }} />
                        </span>
                        <span style={{ color: opportunity.dot }}>{opportunity.readiness}%</span>
                      </>
                    ) : null}
                  </span>
                </button>
              ))}
              <p className="hidden-count">+ 18 kizáró ok · elrejtve</p>
            </div>
            <aside className="detail-panel" aria-live="polite">
              <div className="detail-head">
                <span>Jogosultsági döntés</span>
                <StatusChip opportunity={selected} />
              </div>
              <h2>{selected.title}</h2>
              <div className="detail-badges" aria-label="Döntési kontextus">
                <span className="mini-chip">{selected.confidence}</span>
                <span className="mini-chip">Szabálymotor · AI nélkül</span>
                <span className="mini-chip fresh">Friss forrás</span>
              </div>
              <DetailBlock dot="#C8A35B" label="Miért ez az állapot?" text={selected.why} />
              <div className="detail-block">
                <span className="detail-label">
                  <i style={{ background: selected.dot }} />
                  Hiányok és pótolhatóság
                </span>
                {selected.gaps.length > 0 ? (
                  selected.gaps.map((gap) => (
                    <span className="gap-item" key={gap.label}>
                      <span>{gap.label}</span>
                      <span className="gap-tags">
                        <b className={gap.remediation === 'Pótolható' ? '' : 'review'}>{gap.remediation}</b>
                        {gap.estimate ? <small>{gap.estimate}</small> : null}
                      </span>
                    </span>
                  ))
                ) : (
                  <p>Nincs kizáró ok vagy hiányzó kötelező adat.</p>
                )}
              </div>
              {typeof selected.readiness === 'number' && selected.tasks ? (
                <div className="detail-block readiness-block">
                  <span className="detail-label">
                    <i style={{ background: '#2E7D5B' }} />
                    Beadási felkészültség
                  </span>
                  <span className="readiness-row">
                    <strong>{selected.readiness}%</strong>
                    <small>
                      {selected.tasks.done}/{selected.tasks.total} teendő kész · {selected.tasks.offPlatform} platformon kívüli
                    </small>
                  </span>
                  <span className="progress">
                    <span style={{ width: `${selected.readiness}%`, background: selected.dot }} />
                  </span>
                </div>
              ) : null}
              <div className="detail-block">
                <span className="detail-label">
                  <i style={{ background: '#2E7D5B' }} />
                  Következő lépés
                </span>
                <p>{selected.next}</p>
                <button type="button" className="small-dark-button">
                  Teendő megnyitása <ArrowIcon />
                </button>
              </div>
            </aside>
          </div>
        </div>
      </div>
    </section>
  );
}

function PainSection() {
  return (
    <section className="dark-section pain-section" aria-labelledby="pain-title">
      <DecorativeWords />
      <div className="section-inner">
        <div className="section-copy narrow">
          <SectionLine />
          <h2 id="pain-title" data-reveal>
            A pályázatok többsége nem azért okoz nehézséget, mert nincs lehetőség — hanem mert{' '}
            <em>túl sok a zaj.</em>
          </h2>
        </div>
        <div className="pain-grid" data-reveal>
          {painItems.map((item, index) => (
            <div className="pain-item" key={item}>
              <span>{String(index + 1).padStart(2, '0')}</span>
              <strong>{item}</strong>
            </div>
          ))}
        </div>
        <p className="section-tail" data-reveal>
          A pályázati rendszer mostantól átláthatóbb.
        </p>
      </div>
    </section>
  );
}

function PromiseSection() {
  return (
    <section id="funkciok" className="light-section" aria-labelledby="promise-title">
      <div className="section-inner">
        <div className="section-copy">
          <SectionLine />
          <h2 id="promise-title" data-reveal>
            Az Orienta három dolgot mond meg.
          </h2>
        </div>
        <div className="promise-grid">
          <PromiseCard
            number="Relevancia"
            title="Melyik pályázat jöhet szóba?"
            body="A céged és projektötleted alapján az Orienta rangsorolja a valóban releváns lehetőségeket."
            tone="green"
          />
          <PromiseCard
            number="Bővítés"
            title="Mit kell javítanod?"
            body="Megmutatja, milyen kisebb változtatásokkal válhatsz jogosulttá további pályázatokra — például telephely, tevékenység vagy projektadat módosításával."
            tone="gold"
          />
          <PromiseCard
            number="Fókusz"
            title="Mit engedj el?"
            body="Az explicit kizáró okokat és nem neked szóló pályázatokat érthetően jelzi, hogy ne pazarolj időt rossz irányra."
            tone="red"
          />
        </div>
      </div>
    </section>
  );
}

function HowItWorksSection() {
  return (
    <section id="hogyan" className="dark-section" aria-labelledby="how-title">
      <div className="section-inner">
        <div className="section-copy">
          <SectionLine label="A folyamat" />
          <h2 id="how-title" data-reveal>
            Hogyan működik az Orienta?
          </h2>
        </div>
        <div className="process-grid">
          {processSteps.map((step, index) => (
            <article className="process-card" data-reveal key={step.title}>
              <span className="process-number">{String(index + 1).padStart(2, '0')}</span>
              <ProcessVisual type={step.visual} />
              <h3>{step.title}</h3>
              <p>{step.body}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function BetaAccessSection() {
  return (
    <section id="kinekvalo" className="cream-band" aria-labelledby="access-title">
      <div className="section-inner split-layout">
        <div>
          <SectionLine />
          <h2 id="access-title" data-reveal>
            2026. augusztus 31-ig díjmentesen kipróbálható béta.
          </h2>
          <p data-reveal>
            Az Orienta szűk ügyfélkörrel indul. Olyan cégeket és tanácsadókat keresünk, akik
            szeretnék a pályázati döntéseiket gyorsabban, forrásokra támaszkodva és kevesebb
            bizonytalansággal előkészíteni.
          </p>
        </div>
        <div className="access-list" data-reveal>
          {[
            ['Korai hozzáférés', 'Az elsők között próbálhatod ki. Csatlakozz a várólistához!'],
            ['Szakértői felülvizsgálattal támogatott indulás', 'Az ajánlások mögött ellenőrzött forrásréteg áll.'],
            ['Visszajelzési lehetőség', 'Számít a véleményed a termék fejlesztése során.'],
            ['Folyamatosan bővülő funkciók', 'Céges dosszié és személyre szabott rangsor azonnal. További bővítések hamarosan.']
          ].map(([title, body], index) => (
            <div className="access-item" key={title}>
              <span>{String(index + 1).padStart(2, '0')}</span>
              <div>
                <strong>{title}</strong>
                <p>{body}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function TrustSection() {
  return (
    <section id="megbizhatosag" className="dark-section trust-section" aria-labelledby="trust-title">
      <div className="section-inner">
        <div className="section-copy wide">
          <SectionLine label="Megbízhatóság" />
          <h2 id="trust-title" data-reveal>
            Miért átláthatóbb az Orienta?
          </h2>
          <p data-reveal>
            Fekete dobozos AI-ígéretek helyett ellenőrizhető döntési rétegekre építünk. A rendszer
            segít a dokumentumok megértésében és strukturálásában. A jogosultsági és ajánlási logika
            forráshivatkozásokra, auditálható szabályokra és szabályrendszerre épül.
          </p>
        </div>
        <div className="trust-grid">
          {[
            ['doc', 'Forráshivatkozás magyarázat'],
            ['grid', 'Strukturált szabálylogika'],
            ['human', 'Szakértői felügyelet'],
            ['shield', 'GDPR-tudatos adatkezelés']
          ].map(([icon, text]) => (
            <div className="trust-card" data-reveal key={text}>
              <TrustIcon type={icon} />
              <strong>{text}</strong>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function BeforeAfterSection() {
  return (
    <section id="allapotok" className="cream-band comparison-section" aria-labelledby="compare-title">
      <div className="section-inner">
        <div className="section-copy wide">
          <SectionLine label="A különbség" />
          <h2 id="compare-title" data-reveal>
            A pályázati portálon minden megtalálható. <em>Az Orienta megmutatja, mi számít neked.</em>
          </h2>
        </div>
        <div className="comparison-grid" data-reveal>
          <ComparisonCard
            highlighted
            title="Orienta"
            label="Személyre szabott"
            items={[
              'Cégre szabott rangsor',
              'Jogosultsági logika',
              'Jogosultsági magyarázat',
              'Forráshivatkozások',
              'Dokumentum checklist',
              'On-platform és off-platform akcióterv'
            ]}
          />
          <ComparisonCard
            title="Pályázati portál / kereső"
            label="Hagyományos megközelítés"
            items={[
              'Sok találat',
              'Általános leírások',
              'Hosszú dokumentumok',
              'Kézi értelmezés',
              'Bizonytalan jogosultság',
              'Nincs személyre szabott teendőlista'
            ]}
          />
        </div>
      </div>
    </section>
  );
}

function FinalCtaSection() {
  return (
    <section id="beta" className="dark-section final-cta" aria-labelledby="beta-title">
      <div className="final-glow" aria-hidden="true" />
      <div className="section-inner final-layout">
        <div>
          <SectionLine />
          <h2 id="beta-title" data-reveal>
            Kezdd egy céges dossziéval.
          </h2>
          <p data-reveal>
            Csatlakozz a béta várólistához, és nézzük meg, hogyan tudja az Orienta támogatni a
            pályázati döntéseidet.
          </p>
          <ul className="cta-checks" data-reveal>
            <li>Cégre szabott rangsor.</li>
            <li>Forráshivatkozás és magyarázat minden állításhoz.</li>
            <li>Teendőlista platformon belüli és kívüli lépésekkel.</li>
          </ul>
        </div>
        <WaitlistForm />
      </div>
    </section>
  );
}

function WaitlistForm() {
  const [form, setForm] = useState(initialForm);
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<WaitlistField, string>>>({});

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setMessage('');
    setFieldErrors({});

    if (!isValidEmail(form.email)) {
      const emailError = 'Kérjük, adj meg egy érvényes céges email címet.';
      setStatus('error');
      setFieldErrors({ email: emailError });
      setMessage(emailError);
      return;
    }

    setStatus('loading');

    try {
      const response = await fetch('/api/waitlist', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      const result = await parseWaitlistResponse(response);

      if (!result.ok) {
        if (result.field) {
          setFieldErrors({ [result.field]: result.message });
        }
        throw new Error(result.message);
      }

      setStatus('success');
      setMessage(result.message);
      setForm(initialForm);
    } catch (error) {
      setStatus('error');
      setMessage(error instanceof Error ? error.message : 'Nem sikerült elküldeni. Kérjük, próbáld újra később.');
    }
  }

  if (status === 'success') {
    return (
      <div className="form-card success-card" role="status" aria-live="polite">
        <span className="success-mark">
          <CheckIcon />
        </span>
        <h3>Köszönjük a jelentkezést.</h3>
        <p>{message}</p>
      </div>
    );
  }

  return (
    <form className="form-card" data-reveal onSubmit={onSubmit} noValidate>
      <div className="hp-field" aria-hidden="true">
        <label htmlFor="website">Weboldal</label>
        <input
          id="website"
          name="website"
          tabIndex={-1}
          autoComplete="off"
          value={form.website}
          onChange={(event) => setForm((current) => ({ ...current, website: event.target.value }))}
        />
      </div>
      <div className="field">
        <label htmlFor="name">Név</label>
        <input
          id="name"
          name="name"
          type="text"
          autoComplete="name"
          placeholder="Teljes név"
          value={form.name}
          disabled={status === 'loading'}
          onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
        />
      </div>
      <div className="field-row">
        <div className="field">
          <label htmlFor="email">Céges email</label>
          <input
            id="email"
            name="email"
            type="email"
            autoComplete="email"
            placeholder="nev@ceg.hu"
            required
            value={form.email}
            disabled={status === 'loading'}
            aria-invalid={fieldErrors.email ? 'true' : undefined}
            aria-describedby={fieldErrors.email ? 'email-error' : undefined}
            onChange={(event) => {
              setForm((current) => ({ ...current, email: event.target.value }));
              if (fieldErrors.email) {
                setFieldErrors((current) => ({ ...current, email: undefined }));
              }
            }}
          />
          {fieldErrors.email ? (
            <p id="email-error" className="field-error">
              {fieldErrors.email}
            </p>
          ) : null}
        </div>
        <div className="field">
          <label htmlFor="company">Cégnév</label>
          <input
            id="company"
            name="company"
            type="text"
            autoComplete="organization"
            placeholder="Cég Kft."
            value={form.company}
            disabled={status === 'loading'}
            onChange={(event) => setForm((current) => ({ ...current, company: event.target.value }))}
          />
        </div>
      </div>
      <div className="field-row">
        <div className="field">
          <label htmlFor="role">Szerepkör</label>
          <select
            id="role"
            name="role"
            value={form.role}
            disabled={status === 'loading'}
            onChange={(event) => setForm((current) => ({ ...current, role: event.target.value }))}
          >
            <option value="">Válassz…</option>
            <option value="ceges">Cégvezető / ügyvezető</option>
            <option value="penzugy">Pénzügyi vezető / CFO</option>
            <option value="konyvelo">Könyvelő</option>
            <option value="tanacsado">Pályázati tanácsadó</option>
            <option value="egyeb">Egyéb</option>
          </select>
        </div>
        <div className="field">
          <label htmlFor="companyType">Cég típusa</label>
          <select
            id="companyType"
            name="companyType"
            value={form.companyType}
            disabled={status === 'loading'}
            onChange={(event) => setForm((current) => ({ ...current, companyType: event.target.value }))}
          >
            <option value="">Válassz…</option>
            <option value="mikro">Mikrovállalkozás</option>
            <option value="kkv">KKV</option>
            <option value="nagy">Nagyvállalat</option>
            <option value="startup">Startup</option>
            <option value="tanacsado">Tanácsadó cég</option>
          </select>
        </div>
      </div>
      <fieldset className="interest-field">
        <legend>Fő érdeklődés</legend>
        <div>
          {interests.map((interest) => (
            <button
              key={interest}
              type="button"
              className={form.interest === interest ? 'active' : ''}
              disabled={status === 'loading'}
              onClick={() => setForm((current) => ({ ...current, interest }))}
            >
              {interest}
            </button>
          ))}
        </div>
      </fieldset>
      <button className="submit-button" type="submit" disabled={status === 'loading'}>
        {status === 'loading' ? 'Küldés folyamatban…' : 'Csatlakozom a béta várólistához'}
        <ArrowIcon />
      </button>
      <p className="form-note">A béta kontrollált ügyfélkörrel és szakértői felülvizsgálattal indul. Nem küldünk spamet.</p>
      {message ? (
        <p className={`form-message ${status === 'error' ? 'error' : ''}`} role="status" aria-live="polite">
          {message}
        </p>
      ) : null}
    </form>
  );
}

function SiteFooter() {
  return (
    <footer className="footer">
      <div className="section-inner">
        <div className="footer-top">
          <div className="footer-brand">
            <a href="/#top" className="brand-link">
              <Logo inverse />
              <span>Orienta</span>
            </a>
            <p>A pályázati zajból üzleti döntés. Döntéstámogató és előkészítő rendszer cégeknek és tanácsadóknak.</p>
          </div>
          <nav className="footer-columns" aria-label="Lábléc navigáció">
            <div>
              <h2>Termék</h2>
              {productFooterLinks.map((link) => (
                <a key={link.href} href={link.href}>
                  {link.label}
                </a>
              ))}
            </div>
            <div>
              <h2>Jogi &amp; bizalom</h2>
              {legalFooterLinks.map((link) => (
                <a key={link.href} href={link.href}>
                  {link.label}
                </a>
              ))}
            </div>
          </nav>
        </div>
        <div className="footer-bottom">
          <span>© 2026 Orienta. Minden jog fenntartva.</span>
          <span id="adatvedelem">A feliratkozási adatokat kizárólag a beta kapcsolatfelvételhez használjuk.</span>
          <span>Az Orienta nem garantál támogatást, jogosultságot vagy automatikus beadást — a döntéshozatalt támogatja.</span>
        </div>
      </div>
    </footer>
  );
}

function PromiseCard({ number, title, body, tone }: { number: string; title: string; body: string; tone: string }) {
  return (
    <article className={`promise-card ${tone}`} data-reveal>
      <span className="promise-icon">
        {tone === 'green' ? <CheckIcon /> : tone === 'gold' ? <WarningIcon /> : <StopIcon />}
      </span>
      <p>{number}</p>
      <h3>{title}</h3>
      <span>{body}</span>
    </article>
  );
}

function ProcessVisual({ type }: { type: string }) {
  if (type === 'folder') {
    return (
      <div className="process-visual folder-visual" aria-hidden="true">
        <span />
        <i />
        <b />
      </div>
    );
  }
  if (type === 'chips') {
    return (
      <div className="process-visual chip-visual" aria-hidden="true">
        <span>Beruházás</span>
        <strong>K+F</strong>
        <span>Finanszírozás</span>
        <span>Beszállító</span>
      </div>
    );
  }
  if (type === 'match') {
    return (
      <div className="process-visual match-visual" aria-hidden="true">
        <span />
        <ArrowIcon />
        <strong />
      </div>
    );
  }
  return (
    <div className="process-visual checklist-visual" aria-hidden="true">
      <span />
      <span />
      <i />
    </div>
  );
}

function ComparisonCard({ title, label, items, highlighted = false }: { title: string; label: string; items: string[]; highlighted?: boolean }) {
  return (
    <article className={highlighted ? 'comparison-card highlighted' : 'comparison-card'}>
      <p>{label}</p>
      <h3>{title}</h3>
      <ul>
        {items.map((item) => (
          <li key={item}>
            {highlighted ? <CheckIcon /> : <CrossIcon />}
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </article>
  );
}

function Stat({ dot, value, label, muted = false }: { dot: string; value: string; label: string; muted?: boolean }) {
  return (
    <div className={muted ? 'stat muted' : 'stat'}>
      <span>
        <i style={{ background: dot }} />
        <strong>{value}</strong>
      </span>
      <small>{label}</small>
    </div>
  );
}

function StatusChip({ opportunity }: { opportunity: Opportunity }) {
  return (
    <span className="status-chip" style={{ background: opportunity.chipBg, color: opportunity.chipFg }}>
      <i style={{ background: opportunity.dot }} />
      {opportunity.status}
    </span>
  );
}

function DetailBlock({ dot, label, text }: { dot: string; label: string; text: string }) {
  return (
    <div className="detail-block">
      <span className="detail-label">
        <i style={{ background: dot }} />
        {label}
      </span>
      <p>{text}</p>
    </div>
  );
}

function SectionLine({ label }: { label?: string }) {
  return (
    <div className="section-line" data-reveal>
      <span />
      {label ? <small>{label}</small> : null}
    </div>
  );
}

function DecorativeWords() {
  return (
    <div className="decorative-words" aria-hidden="true">
      {['FELHÍVÁS 3.2.1', 'ÁÚF', 'KIZÁRÓ OK', 'HATÁRIDŐ', 'NYILATKOZAT', 'BEADÁSI ABLAK', 'IGAZOLÁS', 'ELSZÁMOLHATÓ KÖLTSÉG', 'MELLÉKLET 4.', 'ÚTMUTATÓ 2.4'].map(
        (word) => (
          <span key={word}>{word}</span>
        )
      )}
    </div>
  );
}

function Logo({ inverse = false, small = false }: { inverse?: boolean; small?: boolean }) {
  return (
    <svg className={small ? 'logo small' : 'logo'} viewBox="0 0 32 32" aria-hidden="true" data-inverse={inverse ? 'true' : undefined}>
      <circle cx="16" cy="16" r="14.4" fill="none" stroke="currentColor" strokeWidth="1.2" opacity=".5" />
      <path d="M16 4.6 L18.7 16 L16 27.4 L13.3 16 Z" fill="#C8A35B" />
      <path d="M4.6 16 L16 13.3 L27.4 16 L16 18.7 Z" fill="currentColor" opacity=".82" />
      <circle cx="16" cy="16" r="1.7" fill={inverse ? '#101B30' : '#0F1A30'} />
    </svg>
  );
}

function NavIcon({ type }: { type: string }) {
  return (
    <svg width="13" height="13" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {type === 'gauge' ? (
        <>
          <path d="M2.2 9.6a5 5 0 1 1 9.6 0" />
          <path d="M7 8.6l2.3-2.3" />
        </>
      ) : type === 'search' ? (
        <>
          <circle cx="6.2" cy="6.2" r="3.6" />
          <path d="M9 9l3 3" />
        </>
      ) : type === 'doc' ? (
        <>
          <path d="M3.5 1.8h4.6l2.4 2.4v8H3.5z" />
          <path d="M8.1 1.8v2.4h2.4" />
        </>
      ) : type === 'folder' ? (
        <path d="M2 3.5h3.4l1.2 1.4H12v6.6H2z" />
      ) : type === 'checks' ? (
        <>
          <path d="M2.2 4l1.2 1.2L5.6 3" />
          <path d="M7.4 4.4h4.4" />
          <path d="M2.2 9.2l1.2 1.2 2.2-2.2" />
          <path d="M7.4 9.6h4.4" />
        </>
      ) : type === 'vault' ? (
        <>
          <rect x="2" y="2.4" width="10" height="9.2" rx="1.4" />
          <circle cx="7" cy="7" r="2.1" />
        </>
      ) : (
        <path d="M7 1.6l4.4 1.7v2.9c0 2.7-1.8 4.7-4.4 5.6-2.6-.9-4.4-2.9-4.4-5.6V3.3L7 1.6z" />
      )}
    </svg>
  );
}

function TrustIcon({ type }: { type: string }) {
  return (
    <span className="trust-icon">
      {type === 'doc' ? <DocIcon /> : type === 'grid' ? <GridIcon /> : type === 'human' ? <HumanIcon /> : <ShieldIcon />}
    </span>
  );
}

function useRevealOnScroll() {
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return undefined;
    }

    document.documentElement.setAttribute('data-reveal-on', '');
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-in');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -8% 0px' }
    );

    const nodes = Array.from(document.querySelectorAll<HTMLElement>('[data-reveal]'));
    nodes.forEach((node, index) => {
      node.style.transitionDelay = `${Math.min(index % 8, 6) * 55}ms`;
      observer.observe(node);
    });

    return () => observer.disconnect();
  }, []);
}

function useHeroChoreography() {
  useEffect(() => {
    const track = document.getElementById('top');
    const text = document.getElementById('hero-text');
    const mock = document.getElementById('hero-mock');
    const title = document.getElementById('hero-mocktitle');

    if (!track || !text || !mock) {
      return undefined;
    }

    let frame = 0;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
    const ease = (value: number) => 1 - (1 - value) ** 3;

    const reset = () => {
      text.style.opacity = '';
      text.style.transform = '';
      mock.style.transform = '';
      if (title) {
        title.style.opacity = '';
        title.style.transform = '';
      }
    };

    const update = () => {
      frame = 0;
      if (window.innerWidth <= 1240 || reduce.matches) {
        reset();
        return;
      }

      const total = track.offsetHeight - window.innerHeight;
      const progress = total > 0 ? Math.min(Math.max(-track.getBoundingClientRect().top / total, 0), 1) : 0;
      const eased = ease(progress);
      const frameWidth = mock.parentElement?.clientWidth || window.innerWidth;
      const translateX = eased * frameWidth * 0.25;
      const desiredTop = Math.min(112, Math.max(78, window.innerHeight * 0.11));
      const availableHeight = Math.max(420, window.innerHeight - desiredTop - 24);
      const mockHeight = mock.scrollHeight || mock.offsetHeight || availableHeight;
      const fitScale = Math.min(1, Math.max(0.66, availableHeight / mockHeight));
      const fittedTop = (window.innerHeight - mockHeight * fitScale) / 2;
      const translateY = Math.max(0, desiredTop - fittedTop);
      const scale = fitScale * (1 - eased * 0.26);
      const rotateY = eased * 12;
      const translateZ = -eased * 70;
      const textProgress = Math.min(Math.max((progress - 0.14) / 0.44, 0), 1);
      const textEase = ease(textProgress);

      mock.style.transform = `translate(-50%, -50%) translate3d(${translateX.toFixed(1)}px, ${translateY.toFixed(1)}px, ${translateZ.toFixed(1)}px) rotateY(${rotateY.toFixed(2)}deg) scale(${scale.toFixed(3)})`;
      text.style.opacity = textEase.toFixed(3);
      text.style.transform = `translateY(-50%) translateX(${(-28 * (1 - textEase)).toFixed(1)}px)`;

      if (title) {
        const titleOpacity = Math.max(1 - progress / 0.18, 0);
        title.style.opacity = titleOpacity.toFixed(3);
        title.style.transform = `translateY(${(-14 * (1 - titleOpacity)).toFixed(1)}px)`;
      }
    };

    const schedule = () => {
      if (!frame) {
        frame = requestAnimationFrame(update);
      }
    };

    update();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);

    return () => {
      if (frame) {
        cancelAnimationFrame(frame);
      }
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
    };
  }, []);
}

function isValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value.trim());
}

function ArrowIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M3 8h9M9 5l3 3-3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ShieldIcon() {
  return (
    <svg width="17" height="17" viewBox="0 0 18 18" fill="none" aria-hidden="true">
      <path d="M9 2l5.5 2.2v3.6c0 3.4-2.3 6-5.5 7-3.2-1-5.5-3.6-5.5-7V4.2L9 2z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
      <path d="M6.3 8.8l1.7 1.7 3.6-4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path d="M3 10.5l3.5 3.5L17 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function WarningIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path d="M10 7.5v3M10 13h.01" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      <path d="M10 2.2l8 14H2l8-14z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
    </svg>
  );
}

function StopIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <circle cx="10" cy="10" r="7.6" stroke="currentColor" strokeWidth="1.5" />
      <path d="M5.2 5.2l9.6 9.6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function CrossIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

function DocIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
      <path d="M5 3h6l3 3v9H5z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
      <path d="M11 3v3h3" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
    </svg>
  );
}

function GridIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
      <rect x="3" y="3" width="5" height="5" rx="1.2" stroke="currentColor" strokeWidth="1.3" />
      <rect x="10" y="3" width="5" height="5" rx="1.2" stroke="currentColor" strokeWidth="1.3" />
      <rect x="3" y="10" width="5" height="5" rx="1.2" stroke="currentColor" strokeWidth="1.3" />
      <rect x="10" y="10" width="5" height="5" rx="1.2" stroke="currentColor" strokeWidth="1.3" />
    </svg>
  );
}

function HumanIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
      <circle cx="9" cy="6.5" r="3" stroke="currentColor" strokeWidth="1.3" />
      <path d="M3.5 15c0-3 2.5-5 5.5-5s5.5 2 5.5 5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </svg>
  );
}

export default App;
